import React, { useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { SelectButton } from 'primereact/selectbutton';
import { PrimeIcons } from 'primereact/api';
import { Tag } from 'primereact/tag';
import { RequestErrorMessages, useClient } from '../../../hooks/useClient';
import { usePopups } from '../../../context/PopupContext';
import { useOrganizationContext } from '../../../context/OrganizationContext';
import { surveysBasePath, useSurveyStatistics } from '../../../hooks/useSurveys';
import { MAX_EXPORT_PAGE_SIZE } from '../../../config/pagination';
import { SurveyAnswerRow, SurveyLaunch } from '../../../types/survey';
import { PaginatedResponse } from '../../../types/serverTable';
import { exportSurveyAnswersToExcel } from '../../../utils/surveyExportUtils';
import {
  buildRoundTrend,
  formatRoundPeriod,
  getEnabledTextQuestions,
  getQuestionLabel,
  getOtherLanguageTexts,
  getRatingQuestion,
  roundLabel,
} from '../surveyUtils';
import { RatingSummary } from './RatingSummary';
import { QuestionAnswers } from './QuestionAnswers';
import { AdaptiveSelect } from '../../../components/AdaptiveSelect/AdaptiveSelect';
import { SurveyResponsesTable } from './SurveyResponsesTable';
import { SurveyRoundTrend } from './SurveyRoundTrend';
import './SurveyResultsTab.scss';

interface Props {
  launches: SurveyLaunch[];
  onOpenEditor: () => void;
}

type ResultsView = 'summary' | 'responses';

const EXPORT_ERROR_MESSAGES: RequestErrorMessages = {
  summary: 'Error al exportar',
  defaultDetail: 'No se ha podido exportar el Excel',
};

const VIEW_OPTIONS: { label: string; value: ResultsView; icon: string }[] = [
  { label: 'Resumen', value: 'summary', icon: PrimeIcons.CHART_BAR },
  { label: 'Por persona', value: 'responses', icon: PrimeIcons.LIST },
];

function RoundStatusTag({ launch }: { launch: SurveyLaunch }): React.ReactElement {
  return launch.status === 'active' ? (
    <Tag value='En curso' severity='success' rounded />
  ) : (
    <Tag value='Cerrada' severity='secondary' rounded />
  );
}

type RoundOption = { label: string; value: string; launch: SurveyLaunch };

/** D-A13: the closed picker shows the round only, so its width does not depend on the period or the status. */
function renderRoundValue(option: RoundOption | undefined): React.ReactNode {
  return option ? <strong className='survey-round-option'>{option.label}</strong> : null;
}

function renderRoundOption(option: RoundOption | undefined): React.ReactNode {
  if (!option) return null;
  return (
    // Two lines (round and status, then the period), so an item is never wider than the list on a phone
    <span className='survey-round-item'>
      <span className='survey-round-item-top'>
        <strong>{option.label}</strong>
        <RoundStatusTag launch={option.launch} />
      </span>
      <span className='survey-round-item-period'>{formatRoundPeriod(option.launch)}</span>
    </span>
  );
}

export function SurveyResultsTab({ launches, onOpenEditor }: Props): React.ReactElement {
  const { get } = useClient();
  const { showToast } = usePopups() ?? {};
  const { organization } = useOrganizationContext();
  const [selectedId, setSelectedId] = useState<string>();
  const [view, setView] = useState<ResultsView>('summary');
  const [isExporting, setIsExporting] = useState(false);

  // Results are always for one round (default: the newest). Answers of different rounds may answer different questions.
  const launch = launches.find((candidate) => candidate.id === selectedId) ?? launches[0];
  const roundOptions = useMemo(
    (): RoundOption[] => launches.map((item) => ({ label: roundLabel(item.number), value: item.id, launch: item })),
    [launches],
  );
  const locales = organization?.locales ?? [];
  const { data: statistics, isValidating } = useSurveyStatistics(launch?.id, launch?.status === 'active');

  if (!launch) {
    return (
      <div className='survey-results-empty'>
        <i className='pi pi-inbox' aria-hidden />
        <h2>Todavía no hay resultados</h2>
        <p>Inicia la primera ronda para que las personas usuarias de la app respondan la encuesta.</p>
        <Button type='button' label='Preparar la encuesta' icon='pi pi-pencil' outlined onClick={onOpenEditor} />
      </div>
    );
  }

  // SWR keeps the data of the previous round while the new one loads (or after it failed): never show it under this round.
  // `launches` is the same in the statistics of every round, so the comparison of the rounds (D-A10) may use it meanwhile.
  const roundStatistics = statistics?.textAnswers.launchId === launch.id ? statistics : undefined;
  const launchStats = roundStatistics?.launches.find((candidate) => candidate.id === launch.id);
  const answerCount = launchStats?.answerCount ?? 0;
  const textQuestions = getEnabledTextQuestions(launch.questions);
  const rating = getRatingQuestion(launch.questions);
  const answersEndpoint = organization
    ? `${surveysBasePath(organization.id)}/answers?launchId=${encodeURIComponent(launch.id)}`
    : '';

  const handleExport = async (): Promise<void> => {
    setIsExporting(true);
    try {
      const result = await get<PaginatedResponse<SurveyAnswerRow>>(
        `${answersEndpoint}&page=1&pageSize=${MAX_EXPORT_PAGE_SIZE}&export=true`,
        // No timeout: a full export can take longer than the default of the client.
        { errorMessages: EXPORT_ERROR_MESSAGES, timeout: 0 },
      );
      // The client has shown the error already; an empty workbook would look like a round without answers.
      if (!result) return;
      const rows = result.data;
      await exportSurveyAnswersToExcel(rows, launch, `encuesta-ronda-${launch.number}`, locales);
      // One page of MAX_EXPORT_PAGE_SIZE, newest first: say so when the round has more answers.
      const total = result.pagination.totalRecords;
      if (total > rows.length) {
        showToast?.({
          severity: 'warn',
          summary: `El Excel tiene las ${rows.length} respuestas más recientes de ${total}`,
          detail: `Las ${total - rows.length} respuestas más antiguas no están en el archivo. Puedes verlas en «Por persona».`,
          life: 10000,
        });
      }
    } catch (error) {
      console.error(error);
      showToast?.({ severity: 'error', summary: EXPORT_ERROR_MESSAGES.summary, detail: EXPORT_ERROR_MESSAGES.defaultDetail });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className='survey-results'>
      <div className='survey-results-toolbar'>
        <div className='survey-results-round-picker'>
          {/* D-A15: AdaptiveSelect opens a full-screen list on touch devices */}
          <AdaptiveSelect
            inputId='survey-results-round'
            aria-label='Ronda'
            className='survey-results-round'
            panelClassName='survey-results-round-panel'
            value={launch.id}
            options={roundOptions}
            valueTemplate={renderRoundValue}
            itemTemplate={renderRoundOption}
            selectedItemTemplate={renderRoundValue}
            modalTitle='Elige una ronda'
            searchPlaceholder='Buscar ronda'
            onChange={(value: string) => setSelectedId(value)}
          />
          <span className='survey-results-round-meta' data-testid='round-meta'>
            <span>{formatRoundPeriod(launch)}</span>
            <RoundStatusTag launch={launch} />
          </span>
        </div>
        <div className='survey-results-toolbar-actions'>
          <SelectButton
            value={view}
            options={VIEW_OPTIONS}
            optionLabel='label'
            optionValue='value'
            allowEmpty={false}
            itemTemplate={(option: (typeof VIEW_OPTIONS)[number]) => (
              <span className='survey-results-view-option'>
                <i className={option.icon} aria-hidden />
                {option.label}
              </span>
            )}
            onChange={(event) => setView(event.value)}
          />
          <Button
            id='survey-answers-export'
            type='button'
            label='Exportar Excel'
            icon={PrimeIcons.FILE_EXCEL}
            outlined
            loading={isExporting}
            disabled={answerCount === 0 || isExporting}
            onClick={handleExport}
          />
        </div>
      </div>

      {view === 'summary' ? (
        <div className='survey-results-summary'>
          <SurveyRoundTrend
            rows={buildRoundTrend(launches, statistics?.launches ?? [])}
            selectedId={launch.id}
            onSelect={setSelectedId}
          />
          {!roundStatistics ? (
            <div className='survey-results-empty is-inline' role='status'>
              <i className={isValidating ? 'pi pi-spin pi-spinner' : 'pi pi-exclamation-circle'} aria-hidden />
              <p>
                {isValidating ? 'Cargando los resultados de la ronda…' : 'No se han podido cargar los resultados de esta ronda.'}
              </p>
            </div>
          ) : (
            <>
              <div className='survey-results-overview'>
                <section className='survey-results-card survey-results-count' aria-label='Respuestas'>
                  <span className='survey-results-card-label'>Respuestas</span>
                  <span className='survey-results-count-value'>{answerCount}</span>
                  <span className='survey-results-card-hint'>{formatRoundPeriod(launch)}</span>
                </section>
                {rating && (
                  <RatingSummary
                    average={launchStats?.average ?? null}
                    ratingCount={launchStats?.ratingCount ?? 0}
                    distribution={roundStatistics.ratingDistribution}
                  />
                )}
              </div>

              {answerCount === 0 ? (
                <div className='survey-results-empty is-inline'>
                  <i className='pi pi-clock' aria-hidden />
                  <h2>Aún no hay respuestas en esta ronda</h2>
                  <p>
                    {launch.status === 'active'
                      ? 'Las respuestas aparecen aquí en cuanto llegan.'
                      : 'Nadie respondió mientras la ronda estaba abierta.'}
                  </p>
                </div>
              ) : (
                textQuestions.map((question, index) => {
                  const slotAnswers = roundStatistics.textAnswers.slots.find((item) => item.slot === question.slot);
                  const answers = slotAnswers?.answers ?? [];
                  return (
                    <QuestionAnswers
                      key={`${launch.id}-${question.slot}`}
                      position={index + 1}
                      question={getQuestionLabel(question, `Pregunta ${index + 1}`)}
                      otherLanguages={getOtherLanguageTexts(question)}
                      answers={answers}
                      locales={slotAnswers?.locales ?? []}
                      answerCount={slotAnswers?.answerCount ?? answers.length}
                      onShowAll={() => setView('responses')}
                    />
                  );
                })
              )}
            </>
          )}
        </div>
      ) : (
        <SurveyResponsesTable endpoint={answersEndpoint} launch={launch} locales={locales} />
      )}
    </div>
  );
}
